"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { SlidersHorizontal } from "lucide-react";
import type { Dictionary } from "@/dictionaries/types";
import type { NftsResponse } from "@/features/nfts/types";
import NftTile from "@/features/home-v2/NftTile";
import { AppliedFilters, SearchField, SortMenu } from "@/features/home-v2/browse/BrowseControls";
import { Spinner, StatePanel } from "@/features/home-v2/ui";
import { useCurrencies } from "@/features/currencies/hooks/useCurrencies";
import { formatTokenSymbol } from "@/lib/asset-naming";
import BackToTop from "../BackToTop";
import { itemControls } from "../itemControls";
import { RetryButton } from "../parts";
import { useCollectionItems } from "../useCollectionItems";
import ItemFilterPanel from "./ItemFilterPanel";
import { useGridColumns } from "@/hooks/useGridColumns";
import { fullRows } from "@/lib/grid-rows";
type Props = {
  lang: "fa" | "en";
  dict: Dictionary;
  contract: string;
  initialData: NftsResponse;
  pageSize: number;
  network: number | null;
  networkId: number | null;
};
const WIDE = "(min-width: 1024px)";

/**
 * THE WORKS — `useCollectionItems` set as a gallery wall: a quick bar
 * (filters, search, order), the chips of what is in force, and the filters
 * beside the wall on a desktop (open at first) or as a sheet on a phone.
 */
export default function VitrineItems({
  lang,
  dict,
  contract,
  initialData,
  pageSize,
  network
}: Props) {
  const b = dict.browse;
  const cp = dict.collectionPage;
  const isFa = lang === "fa";
  const locale = isFa ? "fa-IR" : "en-US";
  const nf = new Intl.NumberFormat(locale);
  const s = useCollectionItems(contract, lang, initialData, pageSize);
  const {
    grid
  } = s;
  // Whole rows until the list ends (lib/grid-rows); the rest waits for the next batch.
  const cols = useGridColumns();
  const shown = fullRows(grid.items.length, grid.items.length, cols.columns, !grid.hasMore);
  const currenciesQuery = useCurrencies(network ? {
    network_id: network
  } : {}, {
    enabled: Boolean(network),
    staleTime: 10 * 60_000
  });
  const currencies = useMemo(() => (currenciesQuery.data?.data?.currencies ?? []).map(c => ({
    id: c.id,
    symbol: formatTokenSymbol(c.symbol) || c.name
  })), [currenciesQuery.data]);
  const {
    sorts,
    chips
  } = itemControls(s, b, {
    locale,
    currencyName: id => currencies.find(c_0 => c_0.id === id)?.symbol ?? null
  });

  // Open beside the wall on a desktop, closed (a sheet) on a phone.
  const [wide, setWide] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(WIDE);
    setWide(mq.matches);
    setOpen(mq.matches);
    const on = (e: MediaQueryListEvent) => {
      setWide(e.matches);
      setOpen(e.matches);
    };
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const sheetOpen = open && !wide;
  useEffect(() => {
    if (!sheetOpen) return;
    const html = document.documentElement;
    const before = html.style.overflow;
    html.style.overflow = "hidden";
    return () => {
      html.style.overflow = before;
    };
  }, [sheetOpen]);
  const resultsLabel = b.showResults.replace("{count}", s.total != null ? nf.format(s.total) : "…");
  let body: React.ReactNode;
  if (grid.isLoading && grid.items.length === 0) {
    body = <div className="v2-grid" role="status" aria-label={dict.states.loading}>{Array.from({
        length: pageSize
      }, (_, i) => <span key={i} className="v2-cd-shimmer v2-cd-skel-tile" />)}</div>;
  } else if (grid.items.length === 0) {
    body = !grid.isSuccess ? <StatePanel alert title={dict.states.errorTitle} body={dict.states.errorBody} action={<RetryButton label={dict.states.retry} onRetry={grid.reset} />} /> : s.hasActiveFilters ? <StatePanel title={dict.states.filteredEmptyTitle} body={dict.states.filteredEmptyBody} action={<button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={s.clearAll}>{dict.states.clearFilters}</button>} /> : <StatePanel title={dict.discovery.nftsEmptyTitle} body={dict.discovery.nftsEmptyBody} action={<Link className="v2-btn v2-btn--ghost v2-focus" href={`/${lang}/collection`}>{dict.discovery.browseCollections}</Link>} />;
  } else {
    body = <>
        <div ref={cols.ref} className="v2-grid">
          {grid.items.slice(0, shown).map((item, i_0) => <NftTile key={item.id} item={item} lang={lang} noMedia={b.noMedia} marked={i_0 % 4 === 0} showOwner={false} />)}
        </div>
        {grid.hasMore || grid.isFetchingMore ? <div className="vt-more" ref={s.sentinelRef}>
            <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={grid.loadMore} disabled={grid.isFetchingMore} aria-busy={grid.isFetchingMore || undefined}>
              {grid.isFetchingMore ? <Spinner /> : null}
              {grid.isFetchingMore ? b.loadingMore : b.loadMore}
            </button>
            <span className="vt-more-count">{`${nf.format(grid.items.length)} / ${s.total != null ? nf.format(s.total) : "—"}`}</span>
          </div> : null}
        {!grid.hasMore && !grid.isFetchingMore && grid.nextEntryPage != null ? <div className="v2-cd-foot">
            <p className="v2-cd-note">{b.retentionCapNote.replace("{items}", nf.format(grid.items.length))}</p>
            <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={() => s.continueAt(grid.nextEntryPage as number)}>{b.continueNextPage}</button>
          </div> : null}
        {grid.isError ? <StatePanel alert title={dict.states.errorTitle} body={dict.states.errorBody} action={<RetryButton label={dict.states.retry} onRetry={grid.reset} />} /> : null}
      </>;
  }
  return <div className="vt-items">
      <div className="vt-ibar">
        <button type="button" className="v2-bx-trigger vt-ibar-filters v2-focus" data-open={open || undefined} aria-expanded={open} onClick={() => setOpen(v => !v)}>
          <SlidersHorizontal aria-hidden />
          <span>{open && wide ? b.hideFilters : b.filters}</span>
          {s.filterCount ? <b>{nf.format(s.filterCount)}</b> : null}
        </button>
        <SearchField id="vt-items-search" value={s.q} onChange={s.setQuery} placeholder={b.searchNfts} label={b.searchLabel} clearLabel={b.clearSearch} />
        <SortMenu value={s.sort} options={sorts} onChange={s.setSort} label={b.sortLabel} />
        {s.total != null ? <span className="vt-ibar-count" aria-live="polite">{b.items.replace("{count}", nf.format(s.total))}</span> : null}
      </div>
      <AppliedFilters chips={chips} label={b.appliedFilters} clearLabel={b.clearAll} onClear={s.clearAll} />
      <div className="vt-ilay" data-filters={open && wide ? true : undefined}>
        {open && wide ? <ItemFilterPanel s={s} b={b} isFa={isFa} currencies={currencies} sheet={false} resultsLabel={resultsLabel} onClose={close} /> : null}
        {/* The sheet goes to <body>: the tab panel eases in with a transform, which would make
            `position: fixed` mean the panel instead of the screen. */}
        {sheetOpen ? createPortal(<div className="v2 v2-pop-portal">
            <button type="button" className="vt-ft-backdrop" aria-label={b.hideFilters} onClick={close} tabIndex={-1} />
            <ItemFilterPanel s={s} b={b} isFa={isFa} currencies={currencies} sheet resultsLabel={resultsLabel} onClose={close} />
          </div>, document.body) : null}
        <div className="vt-iwall">{body}</div>
      </div>
      <BackToTop enabled={grid.items.length > pageSize} label={cp.backToTop} />
    </div>;
}